import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  CircularProgress,
  TablePagination,
  InputAdornment,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const SUBSCRIPTIONS = gql`
  query AdminSubscriptions($first: Int!, $page: Int, $status: String, $search: String) {
    subscriptions(first: $first, page: $page, status: $status, search: $search) {
      data {
        id
        status
        currency
        amount
        next_charge_date
        card_brand
        card_last4
        retry_count
        user { id full_name email }
        plan { id name }
      }
      paginatorInfo { currentPage lastPage total }
    }
  }
`;

const STATUS_META = {
  active: { label: 'Activa', color: '#34d399', bg: 'rgba(27,147,75,0.16)' },
  grace_period: { label: 'En gracia', color: '#EBBD5C', bg: 'rgba(235,189,92,0.18)' },
  pending: { label: 'Pendiente', color: 'text.primary', bg: 'rgba(128,128,128,0.18)' },
  past_due: { label: 'Vencida', color: '#ff8a8a', bg: 'rgba(178,22,22,0.16)' },
  canceled: { label: 'Cancelada', color: '#c4c8cc', bg: 'rgba(154,160,166,0.18)' },
  inactive: { label: 'Inactiva', color: '#ff8a8a', bg: 'rgba(178,22,22,0.16)' },
};

const STATUS_FILTERS = [
  { value: '', label: 'Todos los estados' },
  { value: 'active', label: 'Activa' },
  { value: 'grace_period', label: 'En gracia' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'canceled', label: 'Cancelada' },
  { value: 'inactive', label: 'Inactiva' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const fmtMoney = (amount, currency) => `$${Number(amount || 0).toLocaleString('es-CO')} ${currency || ''}`.trim();

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Box component="span" sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary', textAlign: 'right' }}>{value}</Box>
    </Stack>
  );
}

// Ítem de suscriptor estilo app de movimientos (Nequi) para mobile: usuario + plan,
// monto a la derecha; tap expande estado, próximo cobro, tarjeta y reintentos. Sin scroll.
function SubscriptionListItem({ subscription, expanded, onToggle }) {
  const meta = STATUS_META[subscription.status] || STATUS_META.pending;
  return (
    <Box sx={(theme) => ({ borderBottom: `2px solid ${theme.palette.stackedRowBorder}`, '&:last-of-type': { borderBottom: 0 } })}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        onClick={onToggle}
        sx={{ px: 1.5, py: 1.25, cursor: 'pointer', '&:active': { backgroundColor: 'surface.overlay' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }} noWrap>
            {subscription.user?.full_name || '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
            {subscription.plan?.name || '—'}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 800, whiteSpace: 'nowrap' }}>
          {fmtMoney(subscription.amount, subscription.currency)}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{ fontSize: 18, color: 'text.disabled', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1)' }}
        />
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={0.75} sx={{ px: 1.5, pb: 1.5 }}>
          <DetailRow label="Estado" value={<Chip size="small" label={meta.label} sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 700 }} />} />
          <DetailRow label="Próximo cobro" value={fmtDate(subscription.next_charge_date)} />
          <DetailRow label="Tarjeta" value={subscription.card_last4 ? `${subscription.card_brand || 'CARD'} •••• ${subscription.card_last4}` : '—'} />
          <DetailRow label="Reintentos" value={String(subscription.retry_count ?? 0)} />
          <DetailRow label="Email" value={subscription.user?.email || '—'} />
        </Stack>
      </Collapse>
    </Box>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

SubscriptionListItem.propTypes = {
  subscription: PropTypes.object.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

/**
 * Listado de suscriptores para administración: tabla con usuario, plan, valor, estado,
 * próximo cobro, tarjeta y reintentos; filtros por estado y búsqueda por nombre/email.
 */
function AdminSubscriptionsList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0); // 0-based (TablePagination)
  const [expanded, setExpanded] = useState(null);
  const first = 25;

  const { data, loading } = useQuery(SUBSCRIPTIONS, {
    variables: { first, page: page + 1, status: status || null, search },
    fetchPolicy: 'cache-and-network',
  });

  const rows = data?.subscriptions?.data || [];
  const total = data?.subscriptions?.paginatorInfo?.total || 0;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por nombre o email"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
        />
        <TextField
          select
          size="small"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          sx={{ minWidth: 200 }}
          SelectProps={{ displayEmpty: true }}>
          {STATUS_FILTERS.map((s) => <MenuItem key={s.value || 'all'} value={s.value}>{s.label}</MenuItem>)}
        </TextField>
      </Stack>

      <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}>
        {isMobile ? (
          <Box>
            {loading && rows.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'gold.main' }} /></Box>
            )}
            {!loading && rows.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>No hay suscriptores.</Box>
            )}
            {rows.map((s) => (
              <SubscriptionListItem
                key={s.id}
                subscription={s}
                expanded={expanded === s.id}
                onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Próximo cobro</TableCell>
                <TableCell>Tarjeta</TableCell>
                <TableCell align="center">Reintentos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} sx={{ color: '#F9BD4E' }} /></TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay suscriptores.</TableCell></TableRow>
              )}
              {rows.map((s) => {
                const meta = STATUS_META[s.status] || STATUS_META.pending;
                return (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>{s.user?.full_name || '—'}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{s.user?.email}</Typography>
                    </TableCell>
                    <TableCell>{s.plan?.name || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtMoney(s.amount, s.currency)}</TableCell>
                    <TableCell><Chip size="small" label={meta.label} sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 700 }} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(s.next_charge_date)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{s.card_last4 ? `${s.card_brand || 'CARD'} •••• ${s.card_last4}` : '—'}</TableCell>
                    <TableCell align="center">{s.retry_count ?? 0}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={first}
          rowsPerPageOptions={[first]}
        />
      </Box>
    </Box>
  );
}

export default AdminSubscriptionsList;
